/**
 * infraService — API calls for branch infrastructure status and provisioning records.
 */

import { apiFetch } from "./apiClient";

const API_URL = import.meta.env.VITE_AUTH_API_URL;

export interface InfraRecord {
  branch_id: string;
  environment_id?: string;
  postgres_id?: string;
  postgres_app_name?: string;
  application_id?: string;
  application_app_name?: string;
  dns_registered: boolean;
  dns_subdomain?: string;
  provisioning_status: string;
  provisioned_at?: string;
  error_message?: string;
  created_at?: string;
  updated_at?: string;
  credentials?: Record<string, unknown>;
}

export interface ServiceCheck {
  exists: boolean;
  status: string | null;     // "running" | "idle" | "error" | "not_found" | null
  app_name?: string;
  database_name?: string;
  details?: string | null;
}

export interface DnsCheck {
  requested: boolean;
  registered_ionos: boolean;
  resolves: boolean;
  resolved_ip: string | null;
  subdomain: string;
}

export interface HealthCheck {
  reachable: boolean;
  protocol?: "https" | "http" | null;
  status_code?: number;
  latency_ms?: number;
  response?: Record<string, unknown>;
  error?: string;
}

export interface InfraStatus {
  branch_id: string;
  branch_name: string;
  backend_url: string;
  infra: InfraRecord | null;
  checks: {
    database: ServiceCheck;
    backend:  ServiceCheck;
    dns:      DnsCheck;
    health:   HealthCheck;
  };
}

/**
 * Fetch live infrastructure status for a branch (Dokploy + DNS + health check).
 */
export async function getBranchInfraStatus(branchId: string): Promise<InfraStatus> {
  const res = await apiFetch(`${API_URL}/provisioning/${branchId}/status`);
  if (!res.ok) throw new Error("Error al obtener estado de infraestructura");
  const json = await res.json();
  return json.data as InfraStatus;
}

/**
 * Fetch the stored infra record (no live checks).
 */
export async function getBranchInfraRecord(branchId: string): Promise<InfraRecord | null> {
  const res = await apiFetch(`${API_URL}/provisioning/${branchId}`);
  if (!res.ok) throw new Error("Error al obtener infra record");
  const json = await res.json();
  return json.data ?? null;
}

/**
 * Trigger full provisioning for a branch.
 * env_name and company_uuid are optional — the backend auto-derives them.
 */
export async function runProvisioning(branchId: string): Promise<void> {
  const res = await apiFetch(`${API_URL}/provisioning/${branchId}/run`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new Error((json as { message?: string }).message ?? "Error al iniciar el aprovisionamiento");
  }
}
